import { Component, OnInit, ViewChild } from '@angular/core';
import { SortDescriptor, orderBy, filterBy, State } from '@progress/kendo-data-query';
import { PageChangeEvent, GridDataResult, GridComponent } from '@progress/kendo-angular-grid';
import { ClientDomainService } from '../../../services/client-domain.service';
import { ClientDomainModel } from '../../../interfaces/client-domain-data';
import { ToastService } from '../../../../shared/services/toast.service';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import {
    NoDataSourceType,
    NoDataTypes,
    NoDataActionType,
} from '../../../../shared/interfaces/no-data';

@Component({
    selector: 'app-domain-management',
    templateUrl: './domain-management.component.html',
    styleUrls: ['./domain-management.component.scss'],
})
export class DomainManagementComponent implements OnInit {
    /* Domain Search */
    domainSearchTerm: string;
    filteredDomains: ClientDomainModel[];
    approvedDomains: ClientDomainModel[];

    /* Domain API */
    loading: boolean;
    error: any;
    domainEntries: ClientDomainModel[];
    domainEntrySort: SortDescriptor[];
    hasName: boolean;

    /* Kendo Grid */
    gridView: GridDataResult;
    headerStyle: { [key: string]: string } = {
        'text-align': 'left',
        'background-color': '#223540',
        color: '#FFFFFF',
        'border-color': '#223540',
    };
    skip = 0;
    pageSize = 10;
    sort: SortDescriptor[] = [];
    sortable = {
        mode: 'single',
        allowUnsort: false,
    };
    scrollable = false;
    type: 'numeric' | 'input' = 'input';
    pageable = {
        refresh: true,
        info: true,
        type: this.type,
        previousNext: true,
        pageSizes: [5, 10, 20],
    };
    gridState: State = {
        sort: [],
        skip: 0,
        take: 10,
    };

    public formGroup: UntypedFormGroup;
    private editedRowIndex: number;
    @ViewChild(GridComponent, { static: false })
    private grid: GridComponent;

    noDataType: NoDataTypes;

    constructor(private toastService: ToastService, private domainService: ClientDomainService) {}

    ngOnInit() {
        scroll(0, 0);
        this.domainService.getDomains().subscribe(
            (data) => {
                this.approvedDomains = data;
            },
            (error) => {
                throw new Error(error);
            }
        );
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'Domains',
            inputSource: NoDataSourceType.table,
        };
        this.domainEntrySort = [{ field: 'domain', dir: 'desc' }];
        this.getDomains();
    }

    /* Domain Methods */
    getDomains() {
        this.loading = true;
        this.domainService.getDomains().subscribe(
            (domains) => {
                this.loading = false;
                this.domainEntries = domains;
                this.loadDomainsInGrid();
            },
            (error) => {
                this.error = error;
                this.loading = false;
                throw new Error(error);
            }
        );
    }

    /* Search */
    filterDomains(): void {
        let searchTermTokens = this.domainSearchTerm.split(' ');
        this.filteredDomains = filterBy(this.domainEntries, {
            logic: 'or',
            filters: [
                {
                    field: 'domain',
                    operator: 'startswith',
                    value: searchTermTokens[0],
                    ignoreCase: true,
                },
            ],
        });
        if (searchTermTokens.length > 1) {
            this.filteredDomains = filterBy(this.filteredDomains, {
                logic: 'or',
                filters: [
                    {
                        field: 'domain',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                ],
            });
        }
        let orderedData = orderBy(this.filteredDomains, this.domainEntrySort);
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
        this.updateGrid();
    }

    /* Kendo Grid */

    // -- Update the Kendo Grid
    updateGrid() {
        let orderedData = orderBy(
            this.filteredDomains ? this.filteredDomains : this.domainEntries,
            this.domainEntrySort
        );
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    loadDomainsInGrid() {
        let orderedData = orderBy(this.domainEntries, this.domainEntrySort);
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    public onStateChange(state: State) {
        this.gridState = state;
        this.loadDomainsInGrid();
    }

    pageChange(event: PageChangeEvent): void {
        this.pageSize = event.take;
        this.skip = event.skip;
        this.updateGrid();
    }

    sortChange(sort: SortDescriptor[]): void {
        this.domainEntrySort = sort;
        this.updateGrid();
    }

    removeHandler({ dataItem }) {
        this.domainService.getEmailCount(dataItem.domain).subscribe(
            (data) => {
                if (+data == 0) {
                    this.domainService.deleteDomain(dataItem.clientDomainKey).subscribe(
                        () => {
                            this.toastService.success('Domain ' + dataItem.domain + ' deleted.');
                            this.getDomains();
                        },
                        () => {
                            this.toastService.error('Error deleting ' + dataItem.domain);
                        }
                    );
                } else {
                    this.toastService.error('Error: Domain has at least one employee');
                }
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    public addDomain() {
        this.closeEditor(this.grid);
        this.formGroup = new UntypedFormGroup({
            domain: new UntypedFormControl('', Validators.pattern('(^.+[.].+$)')),
        });
        this.grid.addRow(this.formGroup);
    }

    public addHandler({ sender, dataItem }) {
        this.closeEditor(sender);
        this.formGroup = new UntypedFormGroup({
            domain: new UntypedFormControl('', Validators.pattern('(^.+[.].+$)')),
        });
        sender.addRow(this.formGroup);
    }

    public editHandler({ sender, rowIndex, dataItem }) {
        this.closeEditor(sender);
        this.formGroup = new UntypedFormGroup({
            clientDomainKey: new UntypedFormControl(dataItem.clientDomainKey),
            clientKey: new UntypedFormControl(dataItem.clientKey),
            domain: new UntypedFormControl(dataItem.domain, Validators.pattern('(^.+[.].+$)')),
        });
        this.editedRowIndex = rowIndex;
        sender.editRow(rowIndex, this.formGroup);
    }

    public saveHandler({ sender, rowIndex, formGroup, isNew }) {
        const domain: ClientDomainModel = formGroup.value;
        let domainCheck = this.approvedDomains.find(
            (foundDomain) => foundDomain.domain == domain.domain
        );
        if (!isNew && domainCheck === undefined) {
            this.domainService.updateDomain(domain).subscribe(
                () => {
                    sender.closeRow(rowIndex);
                    this.toastService.success('Domain ' + domain.domain + ' updated.');
                    this.getDomains();
                },
                (error) => {
                    this.toastService.error('Error updating ' + domain.domain);
                    throw new Error(error);
                }
            );
        } else if (isNew && domainCheck === undefined) {
            this.domainService.insertDomain(domain).subscribe(
                () => {
                    this.toastService.success('Domain ' + domain.domain + ' created.');
                    sender.closeRow(rowIndex);
                    this.getDomains();
                },
                (error) => {
                    this.toastService.error('Error creating ' + domain.domain);
                    throw new Error(error);
                }
            );
        } else {
            this.toastService.error('ERROR: Domain name already exist');
        }
    }

    cancelHandler({ sender, rowIndex }) {
        this.closeEditor(sender, rowIndex);
    }

    private closeEditor(grid, rowIndex = this.editedRowIndex) {
        grid.closeRow(rowIndex);
        this.editedRowIndex = undefined;
        this.formGroup = undefined;
    }
}
